<template>
  <!-- キャリコネクション(グローバルウェイ)連携で必須の項目。いずれも3件まで。
       希望業種・希望職種の選択肢は先方マスタと同一内容（並び順の連番が先方コードになる）、
       希望勤務地・転職希望時期の選択肢は varuna から受け取る先方コード表で、値は先方コード。
       受け取れるまでは選べない（保存値はそのまま） -->
  <div class="edit-form-item">
    <div class="item-name">希望業種（最大3つまで）</div>
    <div class="item-body">
      <el-select
        v-model="hopeIndustryCds"
        multiple
        filterable
        :multiple-limit="hopeSelectMax"
        placeholder="業種を追加"
        style="left: 0; max-width: 100%"
      >
        <el-option-group
          v-for="group in bizreachIndustryGroups"
          :key="group.igCd"
          :label="group.label"
        >
          <el-option
            v-for="item in group.options"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          ></el-option>
        </el-option-group>
      </el-select>
    </div>

    <div class="item-name">希望職種（最大3つまで）</div>
    <div class="item-body">
      <el-select
        v-model="hopeJobCategoryCds"
        multiple
        filterable
        :multiple-limit="hopeSelectMax"
        placeholder="職種を追加"
        style="left: 0; max-width: 100%"
      >
        <el-option-group
          v-for="group in bizreachJobCategoryGroups"
          :key="group.jgCd"
          :label="group.label"
        >
          <el-option
            v-for="item in group.options"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          ></el-option>
        </el-option-group>
      </el-select>
    </div>

    <div class="item-name">希望勤務地（最大3つまで）</div>
    <div class="item-body">
      <el-select
        v-model="hopeWorkAreas"
        multiple
        filterable
        :multiple-limit="hopeSelectMax"
        :disabled="!mastersReady"
        :placeholder="mastersPlaceholder || '勤務地を追加'"
        style="left: 0; max-width: 100%"
      >
        <el-option-group
          v-for="group in hopeAreaGroups"
          :key="group.regionCode"
          :label="group.label"
        >
          <el-option
            v-for="item in group.options"
            :key="item.code"
            :label="item.label"
            :value="item.code"
          ></el-option>
        </el-option-group>
      </el-select>
    </div>
    <div
      v-if="showHopeAreaDefaultNote"
      class="item-name"
      style="font-size: 0.8rem"
    >
      ※ 現住所（{{
        hopeAreaDefaultLabel
      }}）を初期値として設定しています。ご希望の勤務地が異なる場合は修正してください。
    </div>

    <div class="item-name">転職希望時期</div>
    <div class="item-body">
      <el-select
        v-model="hopeTurnTime"
        :disabled="!mastersReady"
        :placeholder="mastersPlaceholder || '選択してください'"
        style="left: 0; max-width: 100%"
      >
        <el-option
          v-for="item in hopeTurnTimeOptions"
          :key="item.code"
          :label="item.label"
          :value="item.code"
        ></el-option>
      </el-select>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useHopeWorkMasters } from '@/composables/format-edit/useHopeWorkMasters'
import {
  HOPE_SELECT_MAX,
  groupHopeAreas,
  hopeAreaLabel
} from '@/utils/format-edit/hopeWorkCondition'

type SelectOption = {
  value: string
  label: string
}

type OptionGroup = {
  igCd?: string
  jgCd?: string
  label: string
  options?: SelectOption[]
}

const props = withDefaults(
  defineProps<{
    formatData: Record<string, unknown>
    bizreachIndustryGroups?: OptionGroup[]
    bizreachJobCategoryGroups?: OptionGroup[]
    // 希望勤務地の初期値に使った先方勤務地コード。空文字なら注記を出さない
    hopeAreaDefaultCode?: string
  }>(),
  {
    bizreachIndustryGroups: () => [],
    bizreachJobCategoryGroups: () => [],
    hopeAreaDefaultCode: ''
  }
)

const emit = defineEmits<{
  (e: 'update-format-data', payload: Record<string, unknown>): void
}>()

function emittedFormatField(key: string) {
  return computed({
    get: () => props.formatData[key],
    set: (value: unknown) => emit('update-format-data', { [key]: value })
  })
}

const hopeWorkAreas = emittedFormatField('hopeWorkAreas')
const hopeTurnTime = emittedFormatField('hopeTurnTime')
const hopeIndustryCds = emittedFormatField('hopeIndustryCds')
const hopeJobCategoryCds = emittedFormatField('hopeJobCategoryCds')

const hopeMasters = useHopeWorkMasters()
const mastersReady = computed(() => hopeMasters.status.value === 'ready')
const mastersPlaceholder = computed(() => {
  if (hopeMasters.status.value === 'error') {
    return '選択肢を読み込めませんでした。時間をおいて開き直してください'
  }
  return mastersReady.value ? '' : '読み込み中…'
})
const hopeAreaGroups = computed(() =>
  groupHopeAreas(hopeMasters.workAreas.value)
)
const hopeTurnTimeOptions = hopeMasters.turnTimes
const hopeSelectMax = HOPE_SELECT_MAX

// 注記は「現住所をそのまま初期値として使っている」状態のときだけ出す。
// ユーザーが選び直したら文言が実態と合わなくなるので消す
const showHopeAreaDefaultNote = computed(() => {
  const areas = props.formatData.hopeWorkAreas
  return (
    Boolean(props.hopeAreaDefaultCode) &&
    Array.isArray(areas) &&
    areas.length === 1 &&
    areas[0] === props.hopeAreaDefaultCode
  )
})

const hopeAreaDefaultLabel = computed(() =>
  hopeAreaLabel(hopeMasters.workAreas.value, props.hopeAreaDefaultCode)
)
</script>
